import React from 'react';
import { useApp } from '../context/AppContext';
import { Search, LayoutGrid, KanbanSquare, CalendarDays, Images, Plus, Filter, ArrowUpDown, BarChart3, RefreshCw } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from './ui/dropdown-menu';

const FILTERS = [
  { id: 'all', label: 'All guests' },
  { id: 'upcoming', label: 'Upcoming (invited + confirmed)' },
  { id: 'pending-release', label: 'Release form not signed' },
  { id: 'no-email', label: 'No email address' },
  { id: 'missing-links', label: 'Missing platform links' },
  { id: 'published', label: 'Published' },
  { id: 'completed', label: 'Completed (recorded / edited / published)' },
  { id: 'potential', label: 'Potential guests' }
];

const views = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'grid', label: 'Grid', icon: LayoutGrid },
  { id: 'kanban', label: 'Kanban', icon: KanbanSquare },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'gallery', label: 'Gallery', icon: Images }
];

export default function TopBar({ onAdd }) {
  const { activeShow, activeTable, activeView, setActiveView, search, setSearch, refreshGuests, statusFilter, setStatusFilter, filteredGuests } = useApp();
  const counts = Object.fromEntries(FILTERS.map(f => [f.id, f.id === statusFilter ? filteredGuests.length : null]));

  return (
    <div className="border-b border-stone-200 bg-white">
      <div className="hidden md:flex items-center justify-between px-5 py-2.5 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: activeShow.color }} />
          <span className="text-[13px] font-medium text-stone-900">{activeShow.name}</span>
          <span className="text-stone-300">/</span>
          <span className="text-[13px] text-stone-600 capitalize">{activeTable}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search guests, episodes..." className="pl-8 h-8 w-64 text-[13px]" />
          </div>
          <Button onClick={refreshGuests} size="sm" variant="outline" className="h-8" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button onClick={onAdd} size="sm" className="h-8 text-white" style={{ background: activeShow.color }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> New guest/prospect
          </Button>
        </div>
      </div>
      {activeTable === 'guests' && (
        <div className="md:hidden px-3 pt-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <Input data-testid="mobile-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search guests, episodes..." className="pl-8 h-9 w-full text-[13px]" />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-3 py-1.5 gap-2">
        <div className="flex items-center gap-1 overflow-x-auto min-w-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(activeTable === 'guests') && views.map(v => {
            const Icon = v.icon;
            const active = activeView === v.id;
            return (
              <button
                key={v.id}
                data-testid={`view-tab-${v.id}`}
                onClick={() => setActiveView(v.id)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12.5px] transition-colors shrink-0',
                  active ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100'
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className={cn(!active && 'hidden sm:inline')}>{v.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 text-stone-600 shrink-0">
          {activeTable === 'guests' && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="grid-filter-btn" className={cn('flex items-center gap-1 px-2 py-1 hover:bg-stone-100 rounded text-[12.5px] max-w-[160px] sm:max-w-none', statusFilter !== 'all' && 'bg-blue-50 text-blue-700 font-medium')}>
                    <Filter className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{statusFilter === 'all' ? 'Filter' : FILTERS.find(f => f.id === statusFilter)?.label || 'Filter'}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel className="text-[11px] uppercase text-stone-500">Show only</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {FILTERS.map(f => (
                    <DropdownMenuItem key={f.id} data-testid={`filter-${f.id}`} onClick={() => { setStatusFilter(f.id); if (activeView !== 'grid') setActiveView('grid'); }} className={cn('text-[12.5px] flex justify-between', statusFilter === f.id && 'bg-stone-100 font-medium')}>
                    <span>{f.label}</span>
                    {counts[f.id] != null && <span className="text-stone-400 text-[11px]">{counts[f.id]}</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button className="hidden sm:flex items-center gap-1 px-2 py-1 hover:bg-stone-100 rounded text-[12.5px]">
                <ArrowUpDown className="w-3.5 h-3.5" /> Sort
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
